import { useForm } from '@tanstack/react-form';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getConfig } from '../config';
import { type Plan, PlanKind, SubscriptionPaymentCycle } from '../entity/api/organization-billing';
import { trackError } from '../error/tracking';
import { getLogger } from '../logger';
import { captureException } from '../logger/capture-exception';
import {
  buildUserData,
  isGTMLoaded,
  storeCheckoutPlan,
  toPrice,
  toRenewalType,
} from '../services/gtm';
import { useAuthUser, useSubscribeOrganization, useSubscriptionPlans } from '../services/storage';
import { tGlobal, tNamespace } from '../services/translation';
import { useToast } from '../shared/contexts/toast-context';
import { calculateMonthlyPrice, getPlanFeatures } from '../shared/subscription';
import { ButtonSwitcher } from './button-switcher';
import { Dialog, DialogContent } from './dialog';
import { PlanCardExtended } from './plan-card-extended';
import { ToastVariant } from './toast';

const tBilling = tNamespace('app.billing.general');
const config = getConfig();

export function TrialSubscriptionModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
}) {
  const { open, onOpenChange, orgId } = props;
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<SubscriptionPaymentCycle>(
    SubscriptionPaymentCycle.Year
  );
  const addToast = useToast();

  // TanStack Query hooks
  const { data: currentUser } = useAuthUser();
  const { data: plans = [] as Plan[] } = useSubscriptionPlans(orgId);
  const subscribeOrganizationMutation = useSubscribeOrganization();

  const scaleYearlyPlan = plans.find(function (plan: Plan) {
    return plan.kind === PlanKind.Scale && plan.paymentCycle === SubscriptionPaymentCycle.Year;
  });

  function handleSubscribe(planId: string) {
    setLoadingPlanId(planId);
    subscribeOrganizationMutation.mutate(
      {
        orgId,
        planId: planId,
        toltReferral: (window as { tolt_referral?: string | null })?.tolt_referral ?? null,
      },
      {
        onSuccess: function (data) {
          setLoadingPlanId(null);
          if (data.data.checkout != null && typeof data.data.checkout === 'string') {
            window.location.href = data.data.checkout;
          } else {
            addToast({
              title: tGlobal('somethingWentWrong'),
              description: 'Unable to redirect to checkout. Please try again.',
              variant: ToastVariant.Error,
              id: Date.now(),
            });
            captureException(
              new Error('Invalid checkout URL received from server'),
              {
                file: 'components/trial-subscription-modal.tsx',
                method: 'handleSubscribe',
                infoMessage: 'checkout URL is null, undefined, or empty',
                extraData: { checkoutUrl: data.data.checkout },
              },
              trackError
            );
          }
        },
        onError: (err) => {
          setLoadingPlanId(null);
          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'components/trial-subscription-modal.tsx',
              method: 'handleSubscribe',
              infoMessage: 'unable to subscribe to plan',
            },
            trackError
          );
        },
      }
    );
  }

  const form = useForm({
    defaultValues: {
      plan: {
        id: plans?.[0]?.providerPlanId ?? '',
      },
    },
    onSubmit: () => {
      handleSubscribe(form.state.values.plan.id);
    },
  });

  // Reset form when plans are loaded
  useEffect(
    function () {
      if (plans.length > 0) {
        form.reset({
          plan: {
            id: plans?.[0]?.providerPlanId ?? '',
          },
        });
      }
    },
    [plans, form]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hasCloseButton
        closeButtonHasShadow
        aria-describedby={undefined}
        className="w-full max-w-[800px]"
      >
        <div className="flex flex-col gap-8">
          {/* Image at the top */}
          <div className="flex justify-center">
            <img
              src="/banner_trial.png"
              alt="trial-subscription"
              className="max-h-[200px] w-full rounded-lg object-cover"
            />
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-end">
            <ButtonSwitcher
              options={[
                { value: SubscriptionPaymentCycle.Month, label: tBilling('monthly') },
                {
                  value: SubscriptionPaymentCycle.Year,
                  label: tBilling('yearlyWithDiscount', {
                    percent: scaleYearlyPlan?.priceDiscount ?? 0,
                  }),
                },
              ]}
              value={selectedBillingCycle}
              onChange={function (value) {
                setSelectedBillingCycle(value as SubscriptionPaymentCycle);
              }}
            />
          </div>

          {/* Plans */}
          <form id="trial-subscription-form">
            <form.Field
              name="plan"
              // eslint-disable-next-line react/no-children-prop
              children={function () {
                const filteredPlans: Plan[] = plans.filter(function (plan: Plan) {
                  return plan.paymentCycle === selectedBillingCycle;
                });

                function getCtaText(_plan: Plan) {
                  return tBilling('startTrial', { trialDays: config.trialDays });
                }

                return (
                  <div className="mb-8 flex items-start gap-6">
                    {filteredPlans.map(function (plan: Plan) {
                      const isBestValue = plan.kind === PlanKind.Scale;

                      return (
                        <PlanCardExtended
                          key={plan.providerPlanId}
                          planId={plan.providerPlanId}
                          title={plan.kind}
                          originalPrice={plan.pricePerMonth}
                          currentPrice={plan.pricePerMonth}
                          discountPercentage={plan.priceDiscount}
                          currency={plan.currency}
                          isBestValue={isBestValue}
                          paymentCycle={plan.paymentCycle}
                          ctaText={getCtaText(plan)}
                          ctaLoading={loadingPlanId === plan.providerPlanId}
                          onCtaClick={function () {
                            storeCheckoutPlan({
                              plan: plan.kind,
                              pricePerMonth: plan.pricePerMonth,
                              priceDiscount: plan.priceDiscount,
                              paymentCycle: plan.paymentCycle,
                              currency: plan.currency,
                            });
                            handleSubscribe(plan.providerPlanId);

                            if (isGTMLoaded()) {
                              window.dataLayer.push({
                                event: 'checkout',
                                plan: plan.kind,
                                // eslint-disable-next-line camelcase
                                renewal_type: toRenewalType(plan.paymentCycle),
                                price: toPrice(plan.pricePerMonth),
                                value: toPrice(
                                  plan.paymentCycle === SubscriptionPaymentCycle.Year
                                    ? plan.pricePerMonth * 12
                                    : plan.pricePerMonth
                                ),
                                currency: plan.currency.toUpperCase(),
                                // eslint-disable-next-line camelcase
                                user_id: currentUser?._id,
                                organizationId: orgId,
                                // eslint-disable-next-line camelcase
                                user_data:
                                  currentUser != null ? buildUserData(currentUser) : undefined,
                              });
                            } else {
                              getLogger().warn('data layer is not initialized');
                              captureException(
                                new Error('data layer is not initialized'),
                                {
                                  file: 'components/trial-subscription-modal.tsx',
                                  method: 'onCtaClick',
                                  infoMessage: 'data layer is not initialized',
                                  extraData: {
                                    plan: plan.kind,
                                    price: calculateMonthlyPrice(
                                      plan.pricePerMonth,
                                      plan.priceDiscount
                                    ),
                                    currency: plan.currency,
                                  },
                                },
                                trackError
                              );
                            }
                          }}
                          features={getPlanFeatures(plan)}
                          className={twMerge('flex-1', isBestValue ? 'md:translate-y-[-16px]' : '')}
                        />
                      );
                    })}
                  </div>
                );
              }}
            />
            <div className="flex flex-col items-center gap-4">
              <a
                href={config.urlWebsitePriceComparison}
                target="_blank"
                rel="noopener noreferrer"
                className="font-body-2-sb text-branding-accent underline hover:text-branding-accent-800"
              >
                {tBilling('seeFullFeatureComparison')} →
              </a>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
